import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import MatchCard from "./MatchCard";
import StateMessage from "./StateMessage";
import { MatchListSkeleton } from "./MatchListParts";
import {
  formatDayLabel,
  formatGameDate,
  formatGameTime,
  localISODate,
} from "../Functions/Formatting";
import { matchStatus } from "../Functions/matchStatus";
import "./HomeTodayMatches.css";

const GAME_SELECT = `
  game_id:id,
  league_id,
  team1_id,
  team2_id,
  team1_score,
  team2_score,
  game_date,
  game_time,
  status,
  t1:teams!team1_id(team_name, logo_url, primary_kit_colors),
  t2:teams!team2_id(team_name, logo_url, primary_kit_colors),
  l:leagues!inner(league_name, theme_color)
`;

function toCardGame(row) {
  return {
    game_id: row.game_id,
    league_id: row.league_id,
    league_name: row.l.league_name,
    theme_color: row.l.theme_color || "#000000",
    team1: row.t1?.team_name,
    team2: row.t2?.team_name,
    team1Logo: row.t1?.logo_url || null,
    team2Logo: row.t2?.logo_url || null,
    team1KitColors: row.t1?.primary_kit_colors || [],
    team2KitColors: row.t2?.primary_kit_colors || [],
    team1Score: row.team1_score,
    team2Score: row.team2_score,
    game_time: row.game_time,
    game_date: row.game_date,
    status: matchStatus(row),
  };
}

export default function HomeTodayMatches({ max = 4 }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [games, setGames] = useState([]);
  // null = today's games; otherwise the YYYY-MM-DD of the next matchday shown instead
  const [nextDate, setNextDate] = useState(null);

  // Local calendar date: `game_date` is a plain date, and games are in the evening
  // local time — a UTC date would roll over to "tomorrow" mid-matchday.
  const todayISO = useMemo(() => localISODate(), []);

  useEffect(() => {
    let cancelled = false;

    const fetchGames = async () => {
      try {
        setLoading(true);
        setErr(null);

        const { data: rows, error } = await supabase
          .from("games")
          .select(GAME_SELECT)
          .eq("game_date", todayISO)
          .eq("is_published", true)
          .eq("leagues.is_published", true)
          .order("game_time", { ascending: true })
          .order("id", { ascending: true })
          .limit(max);

        if (error) throw error;
        if (cancelled) return;

        const today = (rows || []).filter((r) => r.l).map(toCardGame);
        if (today.length > 0) {
          setGames(today);
          setNextDate(null);
          return;
        }

        // Nothing today: show the next published matchday instead of a dead end.
        const { data: upcoming, error: upcomingError } = await supabase
          .from("games")
          .select(GAME_SELECT)
          .gt("game_date", todayISO)
          .eq("is_published", true)
          .eq("leagues.is_published", true)
          .order("game_date", { ascending: true })
          .order("game_time", { ascending: true })
          .order("id", { ascending: true })
          .limit(max);

        if (upcomingError) throw upcomingError;
        if (cancelled) return;

        const list = (upcoming || []).filter((r) => r.l).map(toCardGame);
        const firstDate = list[0]?.game_date || null;
        setGames(list.filter((g) => g.game_date === firstDate));
        setNextDate(firstDate);
      } catch (e) {
        console.error(e);
        if (!cancelled) setErr("No se pudieron cargar los partidos.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchGames();
    return () => {
      cancelled = true;
    };
  }, [todayISO, max]);

  if (loading) {
    return (
      <div className="home-today">
        <MatchListSkeleton rows={2} />
      </div>
    );
  }

  if (err) {
    return (
      <div className="home-today">
        <StateMessage kind="error" title={err}>
          Revisa tu conexión e intenta de nuevo.
        </StateMessage>
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <div className="home-today">
        <StateMessage
          title="No hay partidos hoy"
          linkTo="/partidos"
          linkLabel="Ver calendario"
        >
          Todavía no hay próximos partidos publicados.
        </StateMessage>
      </div>
    );
  }

  return (
    <div className="home-today">
      <p className="home-today-context">
        {nextDate ? (
          <>
            <span className="home-today-flag">Sin partidos hoy</span>
            Próxima jornada: <strong>{formatDayLabel(nextDate)}</strong>
          </>
        ) : (
          <>
            <span className="home-today-flag home-today-flag--live">Hoy</span>
            <strong>{formatDayLabel(todayISO)}</strong>
          </>
        )}
      </p>

      <div className="home-today-list">
        {games.map((game) => (
          <MatchCard
            key={game.game_id}
            displayLeague={true}
            league={game.league_id}
            leagueName={game.league_name}
            themeColor={game.theme_color}
            team1={game.team1}
            team1Logo={game.team1Logo}
            team1KitColors={game.team1KitColors}
            team1Score={game.team1Score}
            team2={game.team2}
            team2Logo={game.team2Logo}
            team2KitColors={game.team2KitColors}
            team2Score={game.team2Score}
            time={formatGameTime(game.game_time)}
            date={formatGameDate(game.game_date)}
            status={game.status}
            gameID={game.game_id}
          />
        ))}
      </div>
    </div>
  );
}
